import { offset } from '@floating-ui/dom';
import { IcoDragAndDrop16 } from '@onefootprint/icons';
import { DragHandle } from '@tiptap/extension-drag-handle-react';
import type { Editor } from '@tiptap/react';
import { useCallback, useMemo, useRef } from 'react';
type BlockDragHandleProps = {
  editor: Editor;
};

// A single global drag handle that floats beside whichever top-level block the pointer is over and
// drags it to reorder.
export const BlockDragHandle = ({
  editor
}: BlockDragHandleProps) => {
  const hoveredBlockRef = useRef<HTMLElement | null>(null);

  // Every DragHandle prop below sits in the wrapper's plugin-registration effect deps, so a fresh
  // identity per render would unregister/re-register the PM plugin on every NotionEditor render,
  // destroying sibling plugin views (e.g. the slash-command popup) mid-use. Keep them all stable.
  const onNodeChange = useCallback(({
    pos
  }: {
    pos: number;
  }) => {
    const dom = pos >= 0 ? editor.view.nodeDOM(pos) : null;
    hoveredBlockRef.current = dom instanceof HTMLElement ? dom : null;
  }, [editor]);
  const onElementDragStart = useCallback((event: DragEvent) => {
    // Stop the dragstart from reaching tiptap-core's window listener, which would treat a drop on
    // another editor as a native move and delete the dragged block from this doc ~10ms later —
    // but the extension cleared the dataTransfer, so nothing is inserted anywhere, and for a table
    // the re-mapped selection then eats the rest of the doc. Stopping it here makes a foreign drop
    // a clean cancel; same-editor reorder rides on view.dragging and is unaffected.
    event.stopPropagation();
    if (!event.dataTransfer) return;

    // The extension builds its drag-image clone at document-body left, so its grab point works out
    // to ≈clientX — pinning the ghost far left of the cursor in a centered editor. Every top-level
    // block shares the editor column's left edge, so shim the extension's setDragImage call (single,
    // synchronous, right after this callback) to clientX - columnLeft; restore it on first use.
    const columnLeft = editor.view.dom.getBoundingClientRect().left;
    const dataTransfer = event.dataTransfer;
    const original = dataTransfer.setDragImage.bind(dataTransfer);
    dataTransfer.setDragImage = (image, _x, y) => {
      dataTransfer.setDragImage = original;
      original(image, event.clientX - columnLeft, y);
    };
  }, [editor]);
  const computePositionConfig = useMemo(() => ({
    placement: 'left-start' as const,
    // left-start anchors the grip to the block's box top-left, but the box is taller than its first
    // text line (headings carry large top padding), which strands the grip above the text. The
    // derivable runs on every reposition, so it reads the hovered block's real padding/line-height
    // and centers the grip on the first line. mainAxis is the gap from the text.
    middleware: [offset(({
      rects
    }) => {
      const block = hoveredBlockRef.current;
      if (!block) return {
        mainAxis: 4
      };
      const style = getComputedStyle(block);
      const paddingTop = Number.parseFloat(style.paddingTop) || 0;
      const lineHeight = Number.parseFloat(style.lineHeight) || rects.floating.height;
      return {
        mainAxis: 4,
        crossAxis: Math.max(0, paddingTop + lineHeight / 2 - rects.floating.height / 2)
      };
    })]
  }), []);
  return (
    // The plugin reparents its element; keep a stable host for React to unmount.
    <div className="contents">
      <DragHandle editor={editor} className="notion-drag-handle" computePositionConfig={computePositionConfig} onNodeChange={onNodeChange} onElementDragStart={onElementDragStart}>
        <span aria-hidden className="notion-drag-handle-grip">
          <IcoDragAndDrop16 color="tertiary" />
        </span>
      </DragHandle>
    </div>
  );
};